import { createQuery } from '@amzn/gsce-shared-react-lib';
import type {
  ListAuditQuestionResponse,
  ListAuditQuestionResponsesRequest,
} from 'src/types/helios/QuestionResponse';
import { questionResponseService, withRetry } from '../../../shared/data';
import { AuditQueryKeys } from './keys';

/**
 * Fetches EVERY question response for one audit from the primary
 * table (DynamoDB).
 *
 * Why this exists alongside {@link useAuditSummaryQuery}: the summary
 * endpoint reads from an eventually-consistent index, so a response
 * the auditor just wrote (comment, answer, media, ERT value) can be
 * missing from the next summary read — the auditor refreshes and
 * their work appears to have vanished until the index catches up.
 * This endpoint reads the source of truth and reflects a write
 * immediately.
 *
 * Division of responsibility on the perform-audit page:
 *   - Summary  → the static skeleton: which questions are in the
 *     audit, their order/content, and server-aggregated metrics.
 *     Index lag is harmless here because these don't change while
 *     the auditor works.
 *   - THIS query → response details (results, comments, naReasons,
 *     mediaIds, ERT values). Authoritative; the summary's copy is
 *     only a first-paint fallback.
 *
 * Uses the legacy REST `questionResponseService` rather than the
 * typed SDK client: these endpoints return epoch-number timestamps
 * which fail the Smithy RFC-3339 deserializer. Same rationale as
 * {@link useAuditQuestionResponseQuery} and the update mutation.
 *
 * NOTE (pagination): the service forwards `limit` / `nextToken`, but
 * callers currently request a single page. Audits larger than the
 * server default page size would leave later questions on
 * summary-only data. Tracked separately — see the perform-audit read
 * path notes.
 *
 * Usage:
 * ```tsx
 * const { data } = useAuditQuestionResponsesQuery(
 *   { businessUnitId, auditId },
 *   { enabled: !!businessUnitId && !!auditId },
 * );
 * ```
 */
export const useAuditQuestionResponsesQuery = createQuery(
  AuditQueryKeys.questionResponses,
  (input: ListAuditQuestionResponsesRequest): Promise<ListAuditQuestionResponse> =>
    fetchAuditQuestionResponses(input),
);

/**
 * Cache key for the list read. `createQuery` maps a request to
 * `[collectionName, input]`; this helper spells that out so the
 * poller can drive the SAME cache entry the page subscribes to
 * instead of issuing its own parallel request.
 */
export function auditQuestionResponsesKey(
  input: ListAuditQuestionResponsesRequest,
): readonly unknown[] {
  return [AuditQueryKeys.questionResponses, input] as const;
}

/**
 * The single fetcher for this endpoint. Shared by the hook above and
 * by the poller's scheduled refresh so there is exactly one network
 * path and one cache entry — two readers of the same list would
 * double the request volume and could disagree about which copy the
 * page renders.
 */
export function fetchAuditQuestionResponses(
  input: ListAuditQuestionResponsesRequest,
): Promise<ListAuditQuestionResponse> {
  return withRetry(() => questionResponseService.getAll(input));
}
